import CMSLayout from '@layouts/CMSLayout';
import {useRouter} from 'next/router';
import FullPageLoader from '@components/FullPageLoader';
import {serverSideTranslations} from 'next-i18next/serverSideTranslations';
import axios from '@lib/axios';
import HeadMeta from '@components/Header/HeadMeta';
import {useEffect, useState} from 'react';
import {getRequireRegistrationUIDS} from '@lib/utils';
import {useRequest} from '@hooks/use-request';
import Lightbox from 'react-18-image-lightbox';
import {ContentContainer} from '@interfaces/content-container';
import {LocaleEnum} from '@enums/locale';
import {GetStaticPropsContext} from 'next';
import {APIRequestConfig} from '@enums/request-config';
import RequiresRegistrationOverlay from '@components/ReuiresRegistrationOverlay';
import {useSession} from 'next-auth/react';
import PageEditLink from '@components/Common/PageEditLink';
import {
  localizeHtmlEnglishLinks,
  rewriteInternalLink,
} from '@lib/localize-html-links';

export default function NaytaSivu({data}: {data: ContentContainer}) {
  const {data: session} = useSession();
  const [isPageRegistered, setPageRegistered] = useState(false);
  const router = useRouter();
  const locale = router.locale as LocaleEnum;
  const {sendRequest, loading} = useRequest(locale);
  if (router.isFallback) {
    return <FullPageLoader />;
  }

  useEffect(() => {
    setPageRegistered(false);
    if (data.requires_registration === 1) {
      sendRequest({
        url: `pages/${data.id}/checkregistration`,
        method: 'post',
        headers: {
          'X-Requires-Registration-Uuids': getRequireRegistrationUIDS(),
        },
        onSuccess: () => {
          setPageRegistered(true);
        },
      });
    }
  }, [router.asPath]);

  const [showPopup, setShowPopup] = useState({index: 0, show: false});
  const [images, setImages] = useState<{source: string; title: string}[]>([]);
  function listener(e: MouseEvent, counter: number) {
    e.preventDefault();
    setShowPopup({index: counter, show: true});
  }

  useEffect(() => {
    if (typeof document !== 'undefined') {
      const containerElement = document.querySelector('#fff');
      if (containerElement && !loading) {
        containerElement.removeEventListener('click', () => {});
        containerElement.addEventListener('click', e => {
          if (!isPageRegistered) {
            e.preventDefault();
            e.stopImmediatePropagation();
          }
        });
      }
    }
  }, [isPageRegistered]);
  useEffect(() => {
    if (typeof document !== 'undefined') {
      const elements = document.querySelectorAll<any>('.image-zoom-link');
      const popupImages: {source: string; title: string}[] = [];
      elements.forEach((element, index) => {
        element.addEventListener('click', (e: MouseEvent) => {
          listener(e, index);
        });
        popupImages.push({
          source: element.attributes.href.textContent,
          title: element.attributes.title?.textContent,
        });
      });
      setImages(popupImages);
    }
  }, [router.asPath]);
  return (
    <CMSLayout
      parentContentId={data.parent_content_container_id}
      contentId={data.second_location_identifier}
      menu={data.menu ?? []}
      breadcrumb={data.breadcrumb}
    >
      {showPopup.show && (
        <Lightbox
          imageCaption={images[showPopup.index].title}
          mainSrc={images[showPopup.index].source}
          nextSrc={images[(showPopup.index + 1) % images.length].source}
          prevSrc={
            images[(showPopup.index + images.length - 1) % images.length].source
          }
          wrapperClassName={'z-index-1200'}
          onCloseRequest={() => setShowPopup({...showPopup, show: false})}
          onMovePrevRequest={() =>
            setShowPopup({
              ...showPopup,
              index: (showPopup.index + images.length - 1) % images.length,
            })
          }
          onMoveNextRequest={() =>
            setShowPopup({
              ...showPopup,
              index: (showPopup.index + 1) % images.length,
            })
          }
        />
      )}
      <HeadMeta
        title={data['seo_page_title']}
        description={data['seo_description']}
        keywords={data['seo_keywords']}
        canonical_url={data['canonical_url']}
      />

      {session && session?.user?.isAdmin && session?.user?.contentEditor && (
        <div className="row justify-content-end">
          <div className="col-2 mb-2">
            <PageEditLink url={`pages/${data['seo_friendly_url']}`} />
          </div>
        </div>
      )}
      <div
        style={{
          pointerEvents:
            isPageRegistered ||
            !data.requires_registration ||
            data.requires_registration === 0
              ? 'initial'
              : 'none',
        }}
      >
        {!isPageRegistered && data.requires_registration === 1 && (
          <RequiresRegistrationOverlay
            verifying={loading}
            fixed={true}
            identifier={data.id}
          />
        )}

        {data.contents.map(content => {
          return (
            <div key={content.id} className={content.div_container}>
              {!!content['upper_div'] && (
                <div
                  className={`${content.upper_div}`}
                  dangerouslySetInnerHTML={{
                    __html: content['upper_div']!,
                  }}
                ></div>
              )}
              <div
                className={content.div_3_classnames}
                style={{width: '100%'}}
                dangerouslySetInnerHTML={{
                  __html: localizeHtmlEnglishLinks(
                    content['column_div_3']
                      ?.replaceAll('&nbsp;', '')
                      ///(https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z\d()]{1,6}\b([-a-zA-Z\d()@:%_+.~#?&=]*)\/((en\/showreference|naytareferenssi|se\/visareferens)\/[-a-zA-Z\d]+))(\/(?!\d+).*)?$/g,
                      ///(https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z\d()]{1,6}\b([-a-zA-Z\d()@:%_+.~#?&=]*)\/((en\/showreference|naytareferenssi|se\/visareferens)\/[a-z-]+))/g,
                      // .replace(
                      //   /\\?"(https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z\d()]{1,6}\b([-a-zA-Z\d()@:%_+.~#?&=]*)\/((en\/showreference|naytareferenssi|se\/visareferens)\/[a-z-]+))(\/(?!\d+).*)?\\?"$/g,
                      //   `$1/${data.id}`
                      // )
                      .replace(
                        /(https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z\d()]{1,6}\b([-a-zA-Z\d()@:%_+.~#?&=]*)\/((en\/showreference|naytareferenssi|se\/visareferens)\/[a-z-]+))/g,
                        `$1/${data.id}`
                      ),
                    locale
                  ),
                }}
              ></div>
            </div>
          );
        })}
      </div>
    </CMSLayout>
  );
}

export async function getStaticProps({params, locale}: GetStaticPropsContext) {
  const slug = params?.slug;

  try {
    const response = await axios.get<any>(`pages/cmspage/${slug}`, {
      headers: {[APIRequestConfig.X_LOCALE]: locale!},
    });
    const data = response.data.data;
    const redirectUrl = data['seo_redirect_url'];
    if (redirectUrl) {
      return {
        redirect: {
          destination: rewriteInternalLink(redirectUrl, locale),
          permanent: true,
        },
      };
    }
    return {
      props: {
        ...(await serverSideTranslations(locale!, ['common'])),
        data,
      },
      revalidate: process.env.NEXT_PUBLIC_REVALIDATE_SECONDS || 84000,
    };
  } catch (e: any) {
    if (e?.response?.status < 500) {
      return {
        notFound: true,
      };
    }
    throw e;
  }
}

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: true,
  };
}
